import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import Header from '../../components/Header';
import { useTranslation } from '../../hooks/useTranslation';
import ModalRegisterClient from './ModalRegisterClient';
import { toast } from 'react-toastify';
import { useCallback, useEffect, useState } from 'react';
import { HOST_API } from '../../config/hosts';
import { tokens } from '../../theme';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { formatCpfCnpj, formatMoney } from '../../utils';
import EditIcon from '@mui/icons-material/Edit';
import api from '../../services/api';

const CreateUser = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Clients list state
  const [searchTerm, setSearchTerm] = useState('');
  const [clients, setClients] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const paginatedClients = clients.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const fetchClients = useCallback(async (term = '') => {
    try {
      setLoadingList(true);
      const cleanTaxvar = term.replace(/\D/g, '');

      const res = await api.get(`${HOST_API}/v1/customer/list?taxvar=${cleanTaxvar}`);

      const data = res.data;
      setClients(data?.list);
    } catch (error) {
      console.error(t('messages.errorFetchingData'), error);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleCreateClient = async (payload) => {
    try {
      setLoading(true);

      await api.post(`${HOST_API}/v1/customer`, payload);

      setOpen(false);
      fetchClients();
      toast.success(t('messages.clientRegisteredSuccess'));
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.errorRegisterClient'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setOpen(true);
  };

  const handleUpdateClient = async (payload) => {
    try {
      setLoading(true);
      await api.put(`${HOST_API}/v1/customer/${selectedClient.id}`, payload);
      setOpen(false);
      setSelectedClient(null);
      fetchClients();
      toast.success(t('messages.clientUpdatedSuccess'));
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.errorUpdateClient'));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpen(false);
    setSelectedClient(null);
  };

  const handleSearch = () => {
    fetchClients(searchTerm);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loadingList) {
    return (
      <Backdrop
        open={loadingList}
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <>
      <Box m="20px">
        <Header title={t('titles.createClient')} subtitle={t('titles.createClientSubtitle')} />

        <Box
          sx={{
            display: 'flex',
            alignContent: 'center',
            justifyContent: 'space-between',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, height: '100%', width: '100%' }}>
            <TextField
              fullWidth
              placeholder={t('forms.cpfCnpj')}
              value={formatCpfCnpj(searchTerm)}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                if (value === '') fetchClients();
              }}
              variant="outlined"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              InputProps={{
                sx: {
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  color: colors.grey[400],
                  '& fieldset': {
                    borderColor: colors.primary[400],
                  },
                  '&:hover fieldset': {
                    borderColor: colors.greenAccent[400],
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.greenAccent[400],
                  },
                },
              }}
              sx={{
                maxWidth: { xs: '100%', md: '400px' },
                height: '100%',
              }}
            />

            <Button
              variant="contained"
              onClick={handleSearch}
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: '12px',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: colors.greenAccent[700],
                },
              }}
            >
              {t('buttons.consult')}
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Ocultado para utilizar no futuro */}
            {/* <Button
              variant="contained"
              sx={{
                backgroundColor: '#fff',
                border: `2px solid ${colors.greenAccent[500]}`,
                color: colors.greenAccent[500],
                fontWeight: 'bold',
                borderRadius: '16px',
                '&:hover': {
                  backgroundColor: colors.greenAccent[700],
                },
              }}
              onClick={() => setOpen(true)}
            >
              <CloudUploadIcon fontSize="large" />
            </Button> */}

            <Button
              variant="contained"
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                fontSize: '12px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: colors.greenAccent[700],
                },
              }}
              onClick={() => setOpen(true)}
            >
              {t('buttons.registerWithoutConsult')}
            </Button>
          </Box>
        </Box>

        {/* Table */}
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 1,
            borderRadius: 2,
            overflowX: 'auto',
          }}
        >
          <Table sx={{ minWidth: { xs: 300, sm: 650 } }}>
            <TableHead sx={{ bgcolor: '#FAFAFA' }}>
              <TableRow>
                <TableCell sx={{ color: '#2C2C2C', fontWeight: 600, fontSize: '16px' }}>
                  {t('customers.name')}
                </TableCell>
                <TableCell sx={{ color: '#2C2C2C', fontWeight: 600, fontSize: '16px' }}>
                  {t('customers.cpfCnpj')}
                </TableCell>
                <TableCell sx={{ color: '#2C2C2C', fontWeight: 600, fontSize: '16px' }}>
                  {t('customers.completeRegistration')}
                </TableCell>
                <TableCell sx={{ width: 50 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedClients.map((client) => (
                <TableRow
                  key={client.id}
                  sx={{
                    '&:hover': {
                      bgcolor: '#FAFAFA',
                    },
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#2C2C2C',
                          fontWeight: 600,
                          mb: 0.5,
                          fontSize: '14px',
                          textTransform: 'uppercase',
                        }}
                      >
                        {client.full_name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: '#737373', fontSize: '14px' }}>
                    {formatCpfCnpj(client.taxvar)}
                  </TableCell>
                  <TableCell sx={{ color: '#737373', fontSize: '14px' }}>Não</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleEditClient(client)}
                      sx={{
                        bgcolor: '#fafafa',
                        width: 36,
                        height: 36,
                        '&:hover': {
                          bgcolor: '#73C43D',
                        },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 18, color: '#00000' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={clients.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Box>

      <ModalRegisterClient
        open={open}
        onClose={handleCloseModal}
        onSubmit={selectedClient ? handleUpdateClient : handleCreateClient}
        loading={loading}
        initialData={selectedClient}
        isEditing={!!selectedClient}
      />
    </>
  );
};

export default CreateUser;
